import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import Spacings from '~/modules/ui-kit/spacings';
import { blueGrey } from '@mui/material/colors';
import MuiTextField from '@mui/material/TextField';
import formMessages from '~/components/form-messages';
import LanguageChipInput from '~/components/LanguageChipInput';
import Tag from '~/components/Tag';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import { CountryCode } from 'libphonenumber-js';
import messages from './messages';
import { isValidPhoneNumber } from '../../utils/phone-number-helpers';
import { useAppForm } from '~/components/Form';
import { z } from 'zod';

type FormValues = {
  phone?: string | null;
  languages?: string[] | null;
};

type UserProfileFormProps = {
  onSubmit: (values: FormValues) => void;
  publisherName?: string | null;
  gender?: string | null;
  congregation?: string | null;
  partnerName?: string | null;
  privilege?: string | null;
  isPioneer?: boolean | null;
  mail?: string | null;
  isLoggedInUsersProfile: boolean;
  initialValues: FormValues;
};

export const UserProfileForm = (props: UserProfileFormProps) => {
  const intl = useIntl();
  const { data: settingsData } = useSettingsByCategory('masterdata');

  const form = useAppForm({
    defaultValues: props.initialValues,
    onSubmit: ({ value }) => {
      props.onSubmit(value);
    },
  });

  const tagStyle = { backgroundColor: blueGrey[50], color: blueGrey[900] };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Spacings.Stack scale="m">
        <Spacings.Stack scale="s">
          <div>
            <Typography variant="h6">
              {props.gender === 'Male' ? (
                <FormattedMessage {...messages.brother} />
              ) : (
                <FormattedMessage {...messages.sister} />
              )}{' '}
              {props.publisherName}
            </Typography>

            <Typography variant="body2">{props.congregation}</Typography>
          </div>
          {props.privilege || props.isPioneer ? (
            <div>
              {props.privilege === 'MinisterialServant' ? (
                <Tag style={tagStyle}>
                  <FormattedMessage {...messages.ministerialServant} />
                </Tag>
              ) : null}

              {props.privilege === 'Elder' ? (
                <Tag style={tagStyle}>
                  <FormattedMessage {...messages.elder} />
                </Tag>
              ) : null}

              {props.isPioneer ? (
                <Tag style={tagStyle}>
                  <FormattedMessage {...messages.pioneer} />
                </Tag>
              ) : null}
            </div>
          ) : null}
        </Spacings.Stack>

        <div>
          <Typography variant="body2">
            {props.partnerName ? (
              <FormattedMessage
                {...messages.servicePartner}
                values={{
                  partnerName: `${props.partnerName}`,
                }}
              />
            ) : (
              <FormattedMessage {...messages.noServicePartner} />
            )}
          </Typography>
        </div>

        <div>
          <form.AppField
            name="phone"
            validators={{
              onBlur: z
                .string({
                  error: intl.formatMessage(formMessages.required),
                })
                .min(1, { message: intl.formatMessage(formMessages.required) })
                .refine(
                  (value) =>
                    isValidPhoneNumber(
                      value || undefined,
                      settingsData?.defaultRegionCallingCode as CountryCode
                    ),
                  { message: intl.formatMessage(messages.invalidPhoneNumber) }
                ),
            }}
          >
            {(field) => (
              <field.TextField
                label={intl.formatMessage(messages.telephoneLabel)}
                placeholder={intl.formatMessage(messages.telephoneLabel)}
                variant="outlined"
              />
            )}
          </form.AppField>
        </div>
        <Spacings.Inline scale="s" alignItems="center">
          <MuiTextField
            disabled
            fullWidth
            id="mailaddress"
            label={intl.formatMessage(messages.emailLabel)}
            defaultValue={props.mail}
            variant="outlined"
          />
          {props.isLoggedInUsersProfile ? (
            <div>
              <Button
                variant="outlined"
                href="/Identity/Account/Manage/Email"
                target="_blank"
              >
                <FormattedMessage {...messages.changeMail} />
              </Button>
            </div>
          ) : null}
        </Spacings.Inline>
        <div>
          <form.Field name="languages">
            {(field) => (
              <LanguageChipInput
                label={intl.formatMessage(messages.languages)}
                selectedLanguages={field.state.value || []}
                onChange={(selectedLanguages) =>
                  field.handleChange(
                    selectedLanguages.map((language) => language.key)
                  )
                }
              />
            )}
          </form.Field>
        </div>

        <div>
          <Button variant="contained" color="primary" type="submit">
            <FormattedMessage {...messages.saveProfileData} />
          </Button>
        </div>
      </Spacings.Stack>
    </form>
  );
};

export default UserProfileForm;
